/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
/* eslint-disable no-empty */
import { ChangeEvent, useState, useRef, useEffect } from 'react';
import { HotelImageType } from 'data/travel-agency/customer/hotelDetails';
import useLightbox from 'hooks/useLightbox';
import Lightbox from 'components/base/LightBox';
import classNames from 'classnames';
import { Container, Card, Col, Row, Table } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { FaCamera } from 'react-icons/fa';
import axios from 'axios';
import { round } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from "react-i18next";
import { toUploadUrl } from 'helpers/utils';

import '../../App.css'
import 'react-h5-audio-player/lib/styles.css';
import { Image } from 'react-bootstrap';

import { IROI } from 'pages/thermography/model/roi.model';
import { IThermogram, createDefaultThermogram } from './model/thermogram.model';
import ThermogramSection from './ThermographicInspectionRecord/ThermogramSection';
import ThermogramMetaDataTable from './ThermographicInspectionRecord/ThermogramMetaDataTable';
import ThermogramReferenceImage from './ThermographicInspectionRecord/ThermogramReferenceImage';

interface ThermalAnomalyImagesItemProps {
	item: HotelImageType;
	handleClick: () => void;
	onChange?: () => void;
	isLast: boolean
}

const DetailsGalleryItem = ({
	item,
	handleClick,
	onChange,
	isLast
}: ThermalAnomalyImagesItemProps) => {
	return (

		<div
			className={classNames(item.classNames, 'cursor-pointer')}
			onClick={handleClick}
		>
			<img src={item.img} alt="" className="rounded-2 img-fluid" />
		</div>
	);
};

interface ThermogramViewProps {
	className?: string;
	mode: string | null;
	title: string;
	thermogram: IThermogram;
	setThermogram: React.Dispatch<React.SetStateAction<IThermogram>>;
	showMetaData?: boolean;
	tempRef?: number;
	deltaT?: number;
}

const ThermogramView = ({className, mode, title, thermogram, setThermogram, showMetaData = true, tempRef = 0, deltaT = 0 }: ThermogramViewProps) => {
	const { t } = useTranslation();
	const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });

	/************************** LightBox and Errors ***********************/
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState(false);
	/************************** End of LightBox and Errors ***********************/


	/************************** IThermogram Metadata ***********************/
	const thermogramInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);

	const [thermogramFile, setThermogramFile] = useState<File | null>(null);
	const [termogramUrl, setTermogramUrl] = useState<string | null>(null);

    const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || '/';
    const thermogramImages: string[] = [];
    if (termogramUrl) {
        thermogramImages.push(termogramUrl);
    }

    const { lightboxProps, openLightbox } = useLightbox(thermogramImages);
	const [audio, setAudio] = useState<string | null>(null);
	const [imageRefFile, setImageRefFile] = useState<File | null>(null);
	const [imageUrlRef, setImageUrlRef] = useState<string | null>(null);

	// Cuida apenas de arquivos selecionados localmente (blob URLs)
	useEffect(() => {
		if (thermogramFile) {
			const objectUrl = URL.createObjectURL(thermogramFile);
			setTermogramUrl(objectUrl);
			return () => URL.revokeObjectURL(objectUrl);
		}
	}, [thermogramFile]);

	useEffect(() => {
		if (imageRefFile) {
			const objectUrl = URL.createObjectURL(imageRefFile);
			setImageUrlRef(objectUrl);
			return () => URL.revokeObjectURL(objectUrl);
		}
	}, [imageRefFile]);

	// Cuida das URLs do servidor — só executa quando não há arquivo local (Edit/View)
	useEffect(() => {
		if (!thermogramFile && thermogram.imagePath && thermogram.imagePath.length > 0) {
			setTermogramUrl(toUploadUrl(thermogram.imagePath, backendBaseUrl));
		}

		if (!imageRefFile && thermogram.imageRefPath) {
			setImageUrlRef(toUploadUrl(thermogram.imageRefPath, backendBaseUrl));
		}

		if (thermogram.audioPath) {
			const filename = thermogram.audioPath.split('/').pop();
			setAudio(`${backendBaseUrl}py-api/uploads/${filename}`);
		}
	}, [thermogram]);

	/******************************* IThermogram ************************** */
	const handleThermogramButtonClick = () => {
		thermogramInputRef.current?.click();
	};

	const onThermogramChange = async (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setThermogram(createDefaultThermogram());
			setAudio('');
			setThermogramFile(e.target.files[0]);
			await handleThermogramUpload(e.target.files[0]);
		}
	};

	const handleThermogramUpload = async (thermogramFile: File) => {

		if (!thermogramFile) {
			setError(tt("pages.thermography.thermogramView.errors.selectFileFirst"));
			return;
		}

		setLoading(true);
		setError('');

		const formData = new FormData();
		formData.append('file', thermogramFile);

		try {

			const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || '/'}py-api/flir/uploadThermogram`, formData, {
				baseURL: '',
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});


			const data = response.data;

			setAudio(data.audio ? `data:audio/wav;base64,${data.audio}` : '');

			let rois = [] as IROI[];
			let index = 1;
			for (const rawRoi of data.metadata.rois) {

				const roi: IROI = {
					id: uuidv4(),
					thermogram: { id: thermogram.id },
					type: rawRoi.type,
					label: rawRoi.label,
					maxTemp: round(rawRoi.tempMax, 1)
				};
				rois.push(roi);
				index++;
			}


			setThermogram(prev => ({
				...prev,
				imagePath: data.imagePath,
				audioPath: data.audioPath,
				minTemp: round(data.metadata.minTemp ?? 0, 1),
				maxTemp: round(data.metadata.tempMax ?? 0, 1),
				avgTemp: round(data.metadata.avgTemp ?? 0, 1),
				selectedRoiId: rois.length > 0 ? rois[0].id : null,
				maxTempRoi: rois.length > 0 ? round(rois[0].maxTemp ?? 0, 1) : 0,
				emissivity: round(data.metadata.emissivity ?? 0, 1),
				subjectDistance: round(data.metadata.distance ?? 0, 1),
				atmosphericTemp: round(data.metadata.atmosphericTemp ?? 0, 1),
				reflectedTemp: round(data.metadata.reflectedTemp ?? 0, 1),
				relativeHumidity: round(data.metadata.relativeHumidity ?? 0, 1),
				cameraLens: data.metadata.cameraLens || '',
				cameraModel: data.metadata.cameraModel || '',
				createDate: data.metadata.createDate || '',
				imageResolution: data.metadata.imageResolution || '',
				latitude: data.metadata.latitude || 0,
				longitude: data.metadata.longitude || 0,
				rois: rois
			}));
			// handleAudio(data.filename);
		} catch (err: unknown) {
			if (err instanceof Error) {
				setError(err.message || tt("pages.thermography.thermogramView.errors.uploadFailed"));
			} else {
				setError(tt("pages.thermography.thermogramView.errors.uploadFailedUnknown"));
			}
			console.error(tt("pages.thermography.thermogramView.errors.uploadErrorLog"), err);
		} finally {
			setLoading(false);
		}
	};

	const handleRoiChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const selectedValue = e.target.value;
		const roi = thermogram.rois ? thermogram.rois.find(r => r.id === selectedValue) : null;

		if (roi) {
			setThermogram(prev => ({
				...prev,
				selectedRoiId: roi.id,
				maxTempRoi: roi.maxTemp
			}));
		}
	};


	/******************************* Image Ref ************************** */
	const handleImageButtonClick = () => {
		imageInputRef.current?.click();
	};

	const onImageRefChange = async (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setImageRefFile(e.target.files[0]);
			handleImageUpload(e.target.files[0]);
		}
	};

	const handleImageUpload = async (imageRefFile: File) => {
		if (!imageRefFile) {
			setError(tt("pages.thermography.thermogramView.errors.selectFileFirst"));
			return;
		}

		setLoading(true);
		setError('');

		const formData = new FormData();
		formData.append('file', imageRefFile);

		try {

			const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || '/'}py-api/flir/uploadImage`, formData, {
				baseURL: '',
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});

			const data = response.data;

			setThermogram(prev => ({
				...prev,
				imageRefPath: data.imagePath.replace('localhost', process.env.REACT_APP_BACKEND_URL)
			}));

		} catch (err: unknown) {

		} finally {
			setLoading(false);
		}
	}


	return (
		<>
			<div className={`position-relative ${className}`}>
				<Container fluid className="d-flex flex-column" style={{ height: '90vh', paddingRight: 0, paddingLeft: 0, paddingTop: 0 }}>
					<ThermogramSection
						title={title}
						mode={mode}
						thermogram={thermogram}
						termogramUrl={termogramUrl}
						onOpenLightbox={() => openLightbox?.()}
						handleRoiChange={(e) => handleRoiChange(e)}
						handleThermogramButtonClick={handleThermogramButtonClick}
						thermogramInputRef={thermogramInputRef}
						onThermogramChange={onThermogramChange}
					>
					</ThermogramSection>


					{showMetaData && (
						<Row className="flex-fill" style={{ height: '45%', overflowY: 'auto' }}>
							{/* Row para a primeira imagem, com flex-grow-1 para ocupar o espaço disponível */}
							<Col className="d-flex align-items-center justify-content-center">
								<ThermogramMetaDataTable
									thermogram={thermogram}
									audio={audio}
									tempRef={tempRef}
									deltaT={deltaT}
								/>
							</Col>
						</Row>
					)}

					{!showMetaData && (
						<Row className="flex-fill" style={{ height: '45%', overflowY: 'hidden' }}>

							{/* Row para a primeira imagem, com flex-grow-1 para ocupar o espaço disponível */}
							<Col className="">
								<ThermogramReferenceImage
									mode={mode}
									imageUrlRef={imageUrlRef}
									onOpenLightbox={() => openLightbox?.()}
									handleImageButtonClick={handleImageButtonClick}
									imageInputRef={imageInputRef}
									onImageRefChange={onImageRefChange}
								/>
							</Col>
						</Row>
					)}
				</Container>
			</div>

			<div>
				<Lightbox {...lightboxProps} />
			</div>
		</>
	);
};

export default ThermogramView;
